import { Component, input, inject, signal, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { InventoryService } from '@servicios/inventory/inventory.service';
import { MessageService } from 'primeng/api';
import { DrawerModule } from 'primeng/drawer';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { InputNumberModule } from 'primeng/inputnumber';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';
import { DividerModule } from 'primeng/divider';
import { TagModule } from 'primeng/tag';

@Component({
  selector: 'app-quick-actions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DrawerModule,
    AutoCompleteModule,
    InputNumberModule,
    DropdownModule,
    ButtonModule,
    SelectButtonModule,
    DividerModule,
    TagModule
  ],
  templateUrl: './quick-actions.component.html',
  styleUrls: ['./quick-actions.component.scss']
})
export class QuickActionsComponent {
  branchId = input<string | null>(null);
  userBranches = input<any[]>([]);
  private fb = inject(FormBuilder);
  private inventoryService = inject(InventoryService);
  private messageService = inject(MessageService);

  visible = signal(false);
  isSaving = signal(false);
  
  // Autocomplete state
  items = signal<any[]>([]);
  selectedItem = signal<any>(null);

  movementForm: FormGroup = this.fb.group({
    itemId: [null, [Validators.required]],
    type: ['IN', [Validators.required]],
    quantity: [1, [Validators.required, Validators.min(1)]],
    reason: ['', [Validators.required]]
  });

  movementTypes = [
    { label: 'Entrada', value: 'IN', icon: 'pi pi-plus-circle' },
    { label: 'Salida', value: 'OUT', icon: 'pi pi-minus-circle' }
  ];

  reasons = [
    { label: 'Compra / Reposición', value: 'Compra / Reposición' },
    { label: 'Uso en Laboratorio', value: 'Uso en Laboratorio' },
    { label: 'Vencimiento', value: 'Vencimiento' },
    { label: 'Rotura / Merma', value: 'Rotura / Merma' },
    { label: 'Devolución', value: 'Devolución' },
    { label: 'Otro', value: 'Otro' }
  ];

  onMovementSuccess = output<void>();

  show() {
    this.visible.set(true);
    this.resetForm();
  }

  hide() {
    this.visible.set(false);
  }

  resetForm() {
    this.movementForm.reset({
      type: 'IN',
      quantity: 1,
      reason: ''
    });
    this.selectedItem.set(null);
  }

  async searchItems(event: any) {
    try {
      const res = await this.inventoryService.getAll('items', { 
        search: event.query, 
        limit: 10,
        branchId: this.branchId() || undefined
      });
      this.items.set(res.data || []);
    } catch (error) {
      console.error('Error searching items:', error);
    }
  }

  onItemSelect(event: any) {
    const item = event.value;
    this.selectedItem.set(item);
    this.movementForm.get('itemId')?.setValue(item.id);
  }

  async saveMovement() {
    if (this.movementForm.invalid) {
      this.movementForm.markAllAsTouched();
      return;
    }

    try {
      this.isSaving.set(true);
      const val = this.movementForm.value;
      
      const payload: any = {
        type: val.type,
        description: `Movimiento rápido: ${val.reason}`,
        branchId: this.branchId() || undefined,
        items: [
          {
            itemId: val.itemId,
            quantity: val.quantity,
            reason: val.reason
          }
        ]
      };

      await this.inventoryService.create('stock-movements', payload);
      
      this.messageService.add({ 
        severity: 'success', 
        summary: 'Éxito', 
        detail: 'Movimiento registrado correctamente' 
      });
      
      this.onMovementSuccess.emit();
      this.hide();
    } catch (error: any) {
      console.error('Error saving movement:', error);
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Error', 
        detail: error.error?.message || 'No se pudo registrar el movimiento' 
      });
    } finally {
      this.isSaving.set(false);
    }
  }
}
